import { Component, computed, inject, signal } from '@angular/core';
import { Brand } from '../../shared/ui/brand';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute } from '@angular/router';
import { catchError, EMPTY, finalize, tap } from 'rxjs';
import { AuthApiService } from '../../core/services/auth-api.service';
import { ApiError, ERROR_TYPE } from '../../core/models/login.model';
import { EndSessionResponse } from '../../core/models/logout.model';

/**
 * Conferma di logout (OIDC RP-Initiated Logout 1.0).
 *
 * L'applicativo non può chiudere la sessione dell'SSO da sé: quel cookie è di sso-bck e
 * solo il browser lo porta. Quindi manda qui l'utente, e questa pagina è l'unico posto in
 * cui la decisione viene presa.
 *
 * La conferma è deliberatamente esplicita: l'uscita è condivisa fra tutte le applicazioni
 * che usano la stessa sessione, non solo da quella che ha avviato la richiesta.
 *
 * Entrambe le risposte — conferma e annullamento — riportano il browser all'applicativo:
 * la specifica non prevede un parametro per "l'utente ha rinunciato", quindi l'applicativo
 * distingue i due casi con una prova successiva a `prompt=none`. Da qui non si costruisce
 * nessuna destinazione: quella arriva da sso-bck, che l'ha validata quando ha creato
 * l'opaque.
 */
@Component({
  selector: 'app-logout',
  imports: [Brand],
  templateUrl: './logout.html',
  styleUrl: './logout.scss',
})
export class Logout {
  private api = inject(AuthApiService);
  private route = inject(ActivatedRoute);

  /** Presente quando si arriva da /sso/v1/end-session. Scade in 180s (lato sso-bck). */
  private readonly opaque = this.route.snapshot.queryParamMap.get('opaque');

  protected readonly hasRequest = this.opaque !== null;

  protected readonly submitting = signal(false);
  protected readonly errorMessage = signal<string | null>(null);
  /** Navigazione top-level verso l'applicativo in corso: i pulsanti restano bloccati. */
  protected readonly redirecting = signal(false);
  /** Deciso, ma senza un posto dove tornare: l'applicativo non ha indicato un URI. */
  protected readonly done = signal<'logout' | 'kept' | null>(null);
  /** L'opaque non è più utilizzabile: scaduto, già consumato, o mai arrivato. */
  protected readonly expired = signal(false);

  protected readonly locked = computed(
    () => this.submitting() || this.redirecting() || this.done() !== null || this.expired(),
  );

  protected confirm(): void {
    this.decide(true);
  }

  protected cancel(): void {
    this.decide(false);
  }

  private decide(confirmed: boolean): void {
    if (this.locked() || this.opaque === null) {
      return;
    }

    this.submitting.set(true);
    this.errorMessage.set(null);

    this.api
      .endSession({ opaque: this.opaque, confirmed })
      .pipe(
        tap((response) => this.handleSuccess(response, confirmed)),
        catchError((error: HttpErrorResponse) => {
          this.handleError(error);
          return EMPTY;
        }),
        finalize(() => this.submitting.set(false)),
      )
      .subscribe();
  }

  private handleSuccess(response: EndSessionResponse, confirmed: boolean): void {
    if (response.redirectUri === null) {
      // L'applicativo non ha registrato dove tornare: si chiude qui, dicendo cosa è stato
      // fatto, invece di lasciare una pagina muta.
      this.done.set(confirmed ? 'logout' : 'kept');
      return;
    }

    // Navigazione top-level, non una fetch: il browser deve arrivare davvero
    // all'applicativo, che da lì verifica l'esito con la sua prova a prompt=none.
    this.redirecting.set(true);
    window.location.assign(response.redirectUri);
  }

  private handleError(error: HttpErrorResponse): void {
    const errorType = (error.error as ApiError | null)?.errorType;

    if (errorType === ERROR_TYPE.invalidOpaque) {
      this.expired.set(true);
      this.errorMessage.set(
        "La richiesta di uscita è scaduta. Se vuoi ancora uscire, riparti dall'applicazione.",
      );
      return;
    }

    if (error.status === 0) {
      this.errorMessage.set('Impossibile contattare il servizio di autenticazione. Riprova.');
      return;
    }

    if (error.status === 400) {
      // Caso tipico: l'id_token_hint non corrisponde alla sessione presente ora, perché
      // nel frattempo il browser si è autenticato come qualcun altro. sso-bck rifiuta
      // invece di chiudere la sessione sbagliata.
      this.expired.set(true);
      this.errorMessage.set(
        'La richiesta non corrisponde alla sessione attualmente aperta in questo browser. ' +
          "Riparti dall'applicazione.",
      );
      return;
    }

    this.errorMessage.set('Si è verificato un errore inatteso. Riprova.');
  }
}
